import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { AiPodCanvasNode } from '../../types/ai-pod-canvas';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';

type PortKind = 'input' | 'output';

interface AiPodNodeCardProps {
  node: AiPodCanvasNode;
  isSelected: boolean;
  isConnecting: boolean;
  onSelect: (nodeId: string) => void;
  onStartConnection: (nodeId: string, outputId: string) => void;
  onEndConnection: (nodeId: string, inputId: string) => void;
  onUpdateNode: (nodeId: string, updates: Partial<AiPodCanvasNode>) => void;
  onDeleteNode: (nodeId: string) => void;
  onBeginDrag?: (nodeId: string, e: React.MouseEvent) => void;
  onHoverInput?: (nodeId: string, inputId: string, rect: DOMRect) => void;
  onLeaveInput?: () => void;
  onAddBranch?: (nodeId: string) => void;
  onPortLayout?: (nodeId: string, portKind: PortKind, portId: string, center: { x: number; y: number }) => void;
}

export const AiPodNodeCard: React.FC<AiPodNodeCardProps> = ({
  node,
  isSelected,
  isConnecting,
  onSelect,
  onStartConnection,
  onEndConnection,
  onUpdateNode,
  onDeleteNode,
  onBeginDrag,
  onHoverInput,
  onLeaveInput,
  onAddBranch,
  onPortLayout,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const outputRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const containerStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    left: node.position.x,
    top: node.position.y,
    width: 192,
  }), [node.position.x, node.position.y]);

  const handleMouseDownHeader = useCallback((e: React.MouseEvent) => {
    onBeginDrag?.(node.id, e);
  }, [node.id, onBeginDrag]);

  const nodeClass = useMemo(() => node.nodeClass ?? node.data?.nodeClass ?? 'conversation', [node.data, node.nodeClass]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(node.id);
  }, [node.id, onSelect]);

  const handlePrimaryInputClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const inputId = node.inputs && node.inputs.length > 0 ? node.inputs[0].id : 'in';
    onEndConnection(node.id, inputId);
  }, [node.id, node.inputs, onEndConnection]);

  const shouldShowPortLabel = useCallback((label: string) => {
    const normalized = label.trim().toLowerCase();
    return !['start', 'input', 'output', 'in', 'out'].includes(normalized);
  }, []);

  const visibleInputs = useMemo(
    () => node.inputs.filter((input) => shouldShowPortLabel(input.name)),
    [node.inputs, shouldShowPortLabel]
  );
  const visibleOutputs = useMemo(
    () => node.outputs.filter((output) => shouldShowPortLabel(output.name)),
    [node.outputs, shouldShowPortLabel]
  );
  const hiddenInputs = useMemo(
    () => node.inputs.filter((input) => !shouldShowPortLabel(input.name)),
    [node.inputs, shouldShowPortLabel]
  );
  const hiddenOutputs = useMemo(
    () => node.outputs.filter((output) => !shouldShowPortLabel(output.name)),
    [node.outputs, shouldShowPortLabel]
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !onPortLayout) return;

    const reportPortCenter = (portKind: PortKind, portId: string, element: HTMLButtonElement | null) => {
      if (!element) return;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const center = {
        x: elementRect.left - containerRect.left + elementRect.width / 2,
        y: elementRect.top - containerRect.top + elementRect.height / 2,
      };
      onPortLayout(node.id, portKind, portId, center);
    };

    node.inputs.forEach((input) => reportPortCenter('input', input.id, inputRefs.current[input.id] ?? null));
    node.outputs.forEach((output) => reportPortCenter('output', output.id, outputRefs.current[output.id] ?? null));
  }, [node.id, node.inputs, node.outputs, node.summary, node.title, node.name, node.nodeClass, onPortLayout]);

  return (
    <div
      ref={containerRef}
      className={`select-none shadow-sm border ${isSelected ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200'} rounded-xl bg-white`}
      style={containerStyle}
      onClick={handleContainerClick}
    >
      <div
        className="flex items-center justify-between rounded-t-xl border-b border-gray-100 bg-white px-4 py-3 cursor-move"
        onMouseDown={handleMouseDownHeader}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0">
          <div className="truncate font-medium text-gray-900">{node.title ?? node.name}</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-400">{nodeClass}</div>
        </div>
      </div>

      <div className="relative flex flex-col gap-3 p-4">
        {node.summary ? <p className="text-xs leading-5 text-gray-500">{node.summary}</p> : null}
        {isConnecting && node.inputs?.length ? (
          <Button tone="custom"
            className="w-full rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
            onClick={handlePrimaryInputClick}
          >
            Connect Here
          </Button>
        ) : null}

        {nodeClass === 'router' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Condition expression</label>
            <TextField tone="default"
              className="w-full px-2 py-1 text-xs border rounded"
              value={String(node.data?.expression ?? '')}
              onChange={(e) => onUpdateNode(node.id, { data: { ...node.data, expression: e.target.value } })}
              placeholder="e.g., outcome === 'qualified'"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {(visibleInputs.length > 0 || visibleOutputs.length > 0) ? (
          <div className="space-y-2">
            {visibleInputs.length > 0 ? (
              <div className="space-y-1.5">
                {visibleInputs.map((input) => (
                  <div key={input.id} className="relative flex h-6 items-center pl-4 pr-2 text-xs text-gray-600">
                    <Button tone="custom"
                      ref={(element) => {
                        inputRefs.current[input.id] = element;
                      }}
                      className="absolute -left-5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-white bg-gray-300 shadow-sm hover:bg-blue-400"
                      title={`Connect to ${input.name}`}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        onHoverInput?.(node.id, input.id, rect);
                      }}
                      onMouseLeave={(e) => { e.stopPropagation(); onLeaveInput?.(); }}
                      onClick={(e) => { e.stopPropagation(); onEndConnection(node.id, input.id); }}
                    />
                    <span className="truncate">{input.name}</span>
                  </div>
                ))}
              </div>
            ) : null}

            {visibleOutputs.length > 0 ? (
              <div className="space-y-1.5">
                {visibleOutputs.map((output) => (
                  <div key={output.id} className="relative flex h-6 items-center justify-end pl-2 pr-4 text-xs text-gray-600">
                    <span className="truncate">{output.name}</span>
                    <Button tone="custom"
                      ref={(element) => {
                        outputRefs.current[output.id] = element;
                      }}
                      className={`absolute -right-5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-white shadow-sm ${isConnecting ? 'bg-blue-500' : 'bg-gray-300'} hover:bg-blue-400`}
                      title={`Start connection from ${output.name}`}
                      onClick={(e) => { e.stopPropagation(); onStartConnection(node.id, output.id); }}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {hiddenInputs.length > 0
          ? hiddenInputs.map((input) => (
              <Button tone="custom"
                key={input.id}
                ref={(element) => {
                  inputRefs.current[input.id] = element;
                }}
                className="absolute -left-5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-white bg-gray-300 shadow-sm hover:bg-blue-400"
                title={`Connect to ${input.name}`}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  onHoverInput?.(node.id, input.id, rect);
                }}
                onMouseLeave={(e) => { e.stopPropagation(); onLeaveInput?.(); }}
                onClick={(e) => { e.stopPropagation(); onEndConnection(node.id, input.id); }}
              />
            ))
          : null}

        {hiddenOutputs.length > 0
          ? hiddenOutputs.map((output) => (
              <Button tone="custom"
                key={output.id}
                ref={(element) => {
                  outputRefs.current[output.id] = element;
                }}
                className={`absolute -right-5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-white shadow-sm ${isConnecting ? 'bg-blue-500' : 'bg-gray-300'} hover:bg-blue-400`}
                title={`Start connection from ${output.name}`}
                onClick={(e) => { e.stopPropagation(); onStartConnection(node.id, output.id); }}
              />
            ))
          : null}

        {nodeClass === 'router' ? (
          <Button tone="custom"
            className="w-full rounded-md border border-dashed border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-600 hover:border-blue-300 hover:text-blue-700"
            onClick={(e) => { e.stopPropagation(); onAddBranch?.(node.id); }}
          >
            Add Branch
          </Button>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button tone="danger-link"
            className="text-xs"
            onClick={(e) => { e.stopPropagation(); onDeleteNode(node.id); }}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AiPodNodeCard;
